import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatDialog } from '@angular/material/dialog';
import { Collaboration } from '@pncl/common-models';
import { Observable, firstValueFrom, of, timer } from 'rxjs';
import { first, map, retry, tap } from 'rxjs/operators';

import { filterNil } from '@ngneat/elf';
import OpenAI from 'openai';
import { environment } from '../../environments/environment';
import {
  CallContext,
  CallProvider,
  DEFAULT_PROVIDER,
  RTCRoomCreationOpts,
} from '../common/interfaces/rtc-interface';
import { GradingResponse } from '../genai/utils/gradingProcessor.util';
import { LLMAPIResponse } from '../genai/utils/lessonProcessor.util';
import {
  ErrorInterceptorSkipHeader,
  httpOptionsWithSkipErrorInterceptor,
} from '../common/functions-and-constant/interceptor';
import { APIAnalyticsInsightResponse, IAnalyticsInsight } from '../models/analytics';
import {
  MakeAllOptionalExcept,
  Permissions,
  PublicSpaceDetails,
  Room,
  Session,
  SessionUser,
  SpaceSettings,
  Visibility,
} from '../models/session';
import { SpaceFolder } from '../models/space-folder';
import { User } from '../models/user';
import {
  RecordingAccess,
  RecordingSummary,
  RecordingTranscriptItem,
  SpaceRecording,
} from '../models/space-recording';
import { EventWithTimingInfo } from '../sessions/session/attendance/attendance-ui-models';
import { RequestAccessService } from '../sessions/session/request-access/request-access.service';
import { WaitingRoomRepoService } from '../sessions/session/request-access/waiting-room/waiting-room-repo.service';
import { SelectSiteBottomSheetComponent } from '../sessions/spaces-view/select-site-bottom-sheet/select-site-bottom-sheet.component';
import { SelectSiteDialogComponent } from '../sessions/spaces-view/select-site-dialog/select-site-dialog.component';
import { IFilterAPI } from '../standalones/components/generic-filters-view/filters.interface';
import { SpaceRepository } from '../state/space.repository';
import { URLService } from './dynamic-url.service';
import { FiltersService } from './filters.service';
import { FLAGS, FlagsService } from './flags.service';
import { NetworkCacheService } from './network-cache.service';
import { FrameBackground, WhiteboardBackground } from './session-shared-data.service';
import { TelemetryService } from './telemetry.service';
import { UiService } from './ui.service';
import { UserService } from './user.service';
import Transcription = Collaboration.Transcription;

export interface CreateSpaceOptions {
  spaceToCloneId?: string;
  siteId?: string;
  templateId?: string;
}

export interface GetSpacesListResponse {
  sessions: Session[];
  totalRecords: number;
  totalPages: number;
  pageNumber: number;
  pageSize: number;
}

/**
 * The spaces switcher's variant of the list response: the user's named sections — pinned spaces and
 * folders, both in full and in their stored order — alongside a page of the spaces in neither. The
 * slices never overlap; the API partitions them server-side.
 */
export interface GetSwitcherSpacesListResponse extends GetSpacesListResponse {
  pinnedSessions: Session[];
  /** Every folder, in sidebar order. Each carries its rows as ids, in row order. */
  folders: SpaceFolder[];
  /** The spaces those ids refer to, in no particular order — resolve them per folder. */
  folderSessions: Session[];
  /**
   * Whether more regular spaces remain. Answered by the API rather than derived from the paging
   * fields above, which it normalises to 0 for an empty list.
   */
  hasMoreSpaces: boolean;
}

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
};

const httpOptionsSkipErrorInterceptor = {
  headers: httpOptions.headers.set(ErrorInterceptorSkipHeader, ''),
};

@Injectable({
  providedIn: 'root',
})
export class SpacesService {
  batchingInterval? = 10000; // default value
  spacesAnalyticsEnabled?: boolean;
  spaceAnalyticsInterval?: NodeJS.Timer | number;

  constructor(
    private http: HttpClient,
    private urlService: URLService,
    private dialog: MatDialog,
    private telemetry: TelemetryService,
    private filtersService: FiltersService,
    private flagsService: FlagsService,
    private spaceRepo: SpaceRepository,
    private userService: UserService,
    private networkCacheService: NetworkCacheService,
    private requestAccessService: RequestAccessService,
    private waitingRoomRepoService: WaitingRoomRepoService,
    private bottomSheet: MatBottomSheet,
    private uiService: UiService,
  ) {}

  /**
   * Opens the "Assign Site" UI — a dialog on desktop, a bottom sheet on mobile —
   * and resolves with the selected siteId, or undefined if the user cancels.
   */
  openSelectSiteDialog(user: User): Observable<string | undefined> {
    if (this.uiService.isMobile.getValue()) {
      return this.bottomSheet
        .open(SelectSiteBottomSheetComponent, {
          panelClass: 'select-site-bottom-sheet-panel',
          disableClose: true,
          autoFocus: false,
          data: { user },
        })
        .afterDismissed()
        .pipe(map((result) => result?.siteId));
    }
    return this.dialog
      .open(SelectSiteDialogComponent, {
        width: '40%',
        panelClass: 'select-site-dialog-container',
        data: { user },
      })
      .afterClosed()
      .pipe(map((result) => result?.siteId));
  }

  createSpace(
    title?: string,
    options?: CreateSpaceOptions,
    skipErrorInterceptor: boolean = false,
  ): Observable<any> {
    // Log that a new space was created.
    this.telemetry.event('user-first-space-visit-organic');
    const spaceUrl = `${this.urlService.getDynamicUrl()}/spaces/create`;
    return this.http
      .post(
        spaceUrl,
        JSON.stringify({
          title,
          spaceToCloneId: options?.spaceToCloneId,
          site: options?.siteId,
          provider: this.flagsService.isFlagEnabled(FLAGS.SPACE_VIDEO_CALL_PROVIDER)
            ? (this.flagsService.featureFlagsVariables?.cpaas_provider?.provider as string) ||
              DEFAULT_PROVIDER
            : DEFAULT_PROVIDER,
          breakoutRoomsEnabled: this.flagsService.isFlagEnabled(FLAGS.BREAKOUT_ROOMS),
          templateId: options?.templateId,
        }),
        {
          ...(skipErrorInterceptor ? httpOptionsSkipErrorInterceptor : httpOptions),
        },
      )
      .pipe(
        tap(async (response) => {
          // if update is successful, clear network cache for space list to get updated list
          // on the next call for the spaces endpoint
          if (response) {
            await this.networkCacheService.clearSpacesListCache();
          }
        }),
      )
      .pipe(retry(3));
  }

  endSession(sessionId: string): Observable<any> {
    const endSessionUrl = `${this.urlService.getDynamicUrl()}/spaces/${sessionId}/endSession`;
    return this.http.post(endSessionUrl, JSON.stringify({}), httpOptions);
  }

  compactSessions(sessionIds: string[]) {
    const compactSessionsUrl = `${this.urlService.getDynamicUrl()}/spaces/compact`;
    return this.http.post(
      compactSessionsUrl,
      JSON.stringify({ sessionIds: sessionIds }),
      httpOptions,
    );
  }

  cloneSessions(data: FormData) {
    const compactSessionsUrl = `${this.urlService.getDynamicUrl()}/spaces/clone`;
    return this.http.post(compactSessionsUrl, data);
  }

  changeProvider(
    data: { spaceIds: string[]; provider: string } | { institutionIds: string[]; provider: string },
  ) {
    const changeProviderUrl = `${this.urlService.getDynamicUrl()}/spaces/changeProvider`;
    return this.http.post<{ numberOfUpdatedSpaces: number }>(changeProviderUrl, data);
  }

  getSession(
    id: string,
  ): Observable<{ session: Session; isAcceptingEmailInvite: boolean | undefined }> {
    const getSessionUrl = `${this.urlService.getDynamicUrl()}/spaces/${id}/`;
    return this.http
      .get<{ session: Session; isAcceptingEmailInvite: boolean | undefined }>(getSessionUrl)
      .pipe(
        tap(async (res) => {
          const user = await firstValueFrom(
            this.userService.user.pipe(
              filterNil(),
              map((u) => u.user),
            ),
          );

          this.spaceRepo.addSpace(
            res.session,
            user,
            this.flagsService.isFlagEnabled(FLAGS.BREAKOUT_ROOMS),
          );
        }),
      );
  }

  getPublicSpaceDetails(id: string): Observable<PublicSpaceDetails> {
    const publicSessionDetailsUrl = `${this.urlService.getDynamicUrl()}/spaces/public-details/${id}/`;
    return this.http.get<PublicSpaceDetails>(publicSessionDetailsUrl);
  }

  getSpaceVideoCallProvider(id: string): Observable<any> {
    const getSpaceVideoCallProviderUrl = `${this.urlService.getDynamicUrl()}/spaces/callProvider/${id}/`;
    return this.http.get(getSpaceVideoCallProviderUrl);
  }

  getSessions(): Observable<any> {
    const getSessionsUrl = `${this.urlService.getDynamicUrl()}/spaces/`;
    return this.http.get(getSessionsUrl);
  }

  /**
   * get spaces ( sessions ) from API
   * @param filters the filters object to include in the request
   * @param pageNumber the current page number
   * @param limit the limit of the results
   * @param skip number of spaces to skip instead of using pageNumber
   * @param withDescSorting if we should get spaces with descending sort
   */
  getSpacesList(
    filters?: IFilterAPI,
    pageNumber?: number,
    limit?: number,
    skip?: number,
    withDescSorting?: boolean,
  ): Observable<GetSpacesListResponse> {
    this.filtersService.logShortSearchQuery(filters?.query ?? filters?.search);

    const encodedFilters = filters ? btoa(encodeURIComponent(JSON.stringify(filters))) : '';

    let query = `filters=${encodedFilters}`;
    if (pageNumber) {
      query += `&pageNumber=${pageNumber}`;
    }

    if (limit) {
      query += `&pageSize=${limit}`;
    }

    if (skip) {
      query += `&skip=${skip}`;
    }

    if (withDescSorting) {
      query += '&sort=desc';
    }

    return this.http.get<GetSpacesListResponse>(
      `${this.urlService.getDynamicUrl()}/spaces?${query}`,
    );
  }

  /**
   * Fetches a page of the spaces switcher's list. The first page also carries every pinned space,
   * which spends part of that page's budget — so ask for as many as the sidebar can show and the
   * API decides how many regular spaces are left to fill it with.
   */
  getSwitcherSpacesList(
    pageNumber: number,
    pageSize: number,
  ): Observable<GetSwitcherSpacesListResponse> {
    const query = `filters=&pageNumber=${pageNumber}&pageSize=${pageSize}&sort=desc&includePinned=true&includeFolders=true`;

    return this.http.get<GetSwitcherSpacesListResponse>(
      `${this.urlService.getDynamicUrl()}/spaces?${query}`,
    );
  }

  /**
   * Replaces the current user's pinned spaces with `spaceIds`, in that order. Pinning, unpinning and
   * reordering are all this one call — send the list you want to end up with.
   */
  setPinnedSpaces(spaceIds: string[]): Observable<unknown> {
    return this.http.put(
      `${this.urlService.getDynamicUrl()}/spaces/pinned`,
      JSON.stringify({ spaceIds }),
      httpOptions,
    );
  }

  get isMyFirstSpace$(): Observable<boolean> {
    return this.http
      .get<{ sessions: any[]; totalRecords: number }>(
        `${this.urlService.getDynamicUrl()}/spaces?pageNumber=1&pageSize=1`,
      )
      .pipe(
        map((data) => data.totalRecords === 1 && data.sessions[0]['title'] === 'My First Space'),
      );
  }

  getSessionAuth(id: string): Observable<Partial<Session>> {
    const getSessionAuthUrl = `${this.urlService.getDynamicUrl()}/spaces/auth/${id}`;
    return this.http.get(getSessionAuthUrl);
  }

  getSessionLobby(id: string): Observable<any> {
    const getSessionLobbyUrl = `${this.urlService.getDynamicUrl()}/spaces/lobby/${id}/`;
    return this.http.get(getSessionLobbyUrl);
  }

  getSessionsAuth(ids: string[]): Observable<any> {
    let getSessionsUrl = `${this.urlService.getDynamicUrl()}/spaces/auth/list?ids=`;
    getSessionsUrl = getSessionsUrl + JSON.stringify(ids);
    return this.http.get(getSessionsUrl);
  }

  updateSession(sessionId: string, session: Partial<Session>): Observable<any> {
    const saveURL = `${this.urlService.getDynamicUrl()}/spaces/${sessionId}`;
    return this.http
      .patch(saveURL, JSON.stringify({ session }), {
        ...httpOptionsWithSkipErrorInterceptor,
        observe: 'response',
      })
      .pipe(
        tap(async (response) => {
          // if update session is successful, clear network cache for space list to get updated list
          if (response.ok) {
            await this.networkCacheService.clearSpacesListCache();
          }
        }),
      )
      .pipe(
        retry({
          count: 3,
          delay: (error) => {
            // Don't retry on validation errors (4xx)
            if (error.status >= 400 && error.status < 500) {
              throw error;
            }
            // Retry on server errors (5xx) or network errors after 1 second
            return timer(1000);
          },
        }),
      );
  }

  updateSessionUsers(
    sessionId: string,
    addUsers: SessionUser[] = [],
    removeUsers: SessionUser[] = [],
    inviteText = '',
    sendEmail = true,
    notifyNewlyAddedOnly = true,
  ) {
    if (removeUsers.length > 0) {
      this.waitingRoomRepoService.emitUsersRemovedFromSpace(removeUsers.map((user) => user._id));
    }
    const saveURL = `${this.urlService.getDynamicUrl()}/spaces/${sessionId}/updateUsers`;
    const updateObject: any = {
      addUsers,
      removeUsers,
      inviteText,
      sendEmail,
      notifyNewlyAddedOnly,
    };
    return this.http
      .patch(saveURL, JSON.stringify(updateObject), {
        ...httpOptions,
        observe: 'response',
      })
      .pipe(
        tap(async (response) => {
          // if update is successful, clear network cache for space list to get updated users
          // on the next call for the spaces endpoint
          if (response.ok) {
            await this.networkCacheService.clearSpacesListCache();
          }
        }),
      );
  }

  sendRecordingURL(sessionId: string, userId: string, recordingId: string): Observable<any> {
    const URL = `${this.urlService.getDynamicUrl()}/spaces/sendRecordingURL/${sessionId}`;
    return this.http.post<any>(URL, JSON.stringify({ recordingId }), httpOptions).pipe(retry(3));
  }

  updateVisibility(sessionId: string, visibility: Visibility) {
    if (
      visibility === Visibility.PUBLIC &&
      this.requestAccessService.shouldShowAccessRequestsValue()
    ) {
      this.requestAccessService.approveAll();
    }
    const saveURL = `${this.urlService.getDynamicUrl()}/spaces/${sessionId}`;
    const body = { session: { visibility: visibility } };
    return this.http
      .patch(saveURL, JSON.stringify(body), {
        ...httpOptions,
        observe: 'response',
      })
      .pipe(
        tap(async (response) => {
          // if update is successful, clear network cache for space list to get updated visibility
          // on the next call for the spaces endpoint
          if (response.ok) {
            await this.networkCacheService.clearSpacesListCache();
          }
        }),
      );
  }

  updateSettings(
    sessionId: string,
    settings: SpaceSettings,
    skipErrorInterceptor: boolean = false,
  ) {
    if (!settings.enableWaitingRoom) {
      this.waitingRoomRepoService.emitAcceptAllInWR();
    }

    const url = `${this.urlService.getDynamicUrl()}/spaces/${sessionId}/settings`;
    const body = { settings };

    return this.http
      .patch(url, JSON.stringify(body), {
        ...(skipErrorInterceptor ? httpOptionsSkipErrorInterceptor : httpOptions),
        observe: 'response',
      })
      .pipe(
        tap(async (response) => {
          // if update is successful, clear network cache for space list to get updated list
          // on the next call for the spaces endpoint
          if (response.ok) {
            await this.networkCacheService.clearSpacesListCache();
          }
        }),
      );
  }

  updateLockState(sessionId: string, isLocked: boolean) {
    const saveURL = `${this.urlService.getDynamicUrl()}/spaces/${sessionId}`;
    const body = { session: { isLocked: isLocked } };
    return this.http.patch(saveURL, JSON.stringify(body), {
      ...httpOptions,
      observe: 'response',
    });
  }

  updateSessionPermissions(
    sessionId: string,
    permissions: Permissions,
    skipErrorInterceptor: boolean = false,
  ) {
    const saveURL = `${this.urlService.getDynamicUrl()}/spaces/${sessionId}`;
    const body = { session: { sessionPermissions: permissions } };
    return this.http.patch(saveURL, JSON.stringify(body), {
      ...(skipErrorInterceptor ? httpOptionsSkipErrorInterceptor : httpOptions),
      observe: 'response',
    });
  }

  resetSpaceBackgroundToDefault(): Observable<boolean> {
    const spaceId = this.spaceRepo.activeSpaceId;
    if (!spaceId) {
      return of(false);
    }

    const defaultBackground = {
      pattern: WhiteboardBackground.DOT,
      color: '#FCFCFD',
    };
    return this.updateBackgroundDefault(this.spaceRepo.activeSpaceId ?? '', defaultBackground).pipe(
      first(),
      tap((res) => {
        if (res.ok) {
          if (this.spaceRepo.activeSpace) {
            const updatedSpace = this.spaceRepo.activeSpace;
            updatedSpace.sessionDefaultFramesBackground = defaultBackground;
            this.spaceRepo.updateSpace(updatedSpace._id, updatedSpace);
          }
        }
      }),
      map((res) => res.ok),
    );
  }

  updateBackgroundDefault(sessionId: string, background: FrameBackground) {
    const saveURL = `${this.urlService.getDynamicUrl()}/spaces/${sessionId}`;
    const body = { session: { sessionDefaultFramesBackground: background } };
    return this.http.patch(saveURL, JSON.stringify(body), {
      ...httpOptions,
      observe: 'response',
    });
  }

  updateUsersPermissions(sessionId: string, users: SessionUser[]) {
    const saveURL = `${this.urlService.getDynamicUrl()}/spaces/${sessionId}`;
    const body = { session: { users: users } };
    return this.http.patch(saveURL, JSON.stringify(body), {
      ...httpOptions,
      observe: 'response',
    });
  }

  /**
   * Sends https request to update session's title if the title
   * is empty a new random title is set.
   * @param sessionId the id of the session to be modified.
   * @param title the new title of the session.
   */
  updateTitle(sessionId: string, title: string) {
    const saveURL = `${this.urlService.getDynamicUrl()}/spaces/${sessionId}`;
    const body = { session: { title: title } };
    return this.http
      .patch(saveURL, JSON.stringify(body), {
        ...httpOptions,
        observe: 'response',
      })
      .pipe(
        tap(async (response) => {
          // if update is successful, clear network cache for space list to get updated title
          // on the next call for the spaces endpoint
          if (response.ok) {
            await this.networkCacheService.clearSpacesListCache();
          }
        }),
      );
  }

  updateSpaceVideoCallProvider(sessionId: string, provider: string) {
    const saveURL = `${this.urlService.getDynamicUrl()}/spaces/${sessionId}`;
    const body = { session: { provider: provider } };
    return this.http.patch(saveURL, JSON.stringify(body), {
      ...httpOptions,
      observe: 'response',
    });
  }

  deleteSession(id: string): Observable<any> {
    const deleteSessionUrl = `${this.urlService.getDynamicUrl()}/spaces/${id}`;
    return this.http.delete(deleteSessionUrl).pipe(
      tap(async (response) => {
        // if update is successful, clear network cache for space list to get updated list
        // on the next call for the spaces endpoint
        if (response) {
          await this.networkCacheService.clearSpacesListCache();
        }
      }),
    );
  }

  sendAnalyticsEvents(
    currentSessionId: string,
    analyticsEvents: Array<any>,
    realTimeInsights: Array<any>,
  ) {
    const spaceId = this.spaceRepo.activeSpace?._id;
    if (!spaceId) {
      return;
    }
    const url = `${this.urlService.getDynamicUrl()}/rtevents/event/${spaceId}`;
    const body = {
      currentSessionId,
      analyticsEvents,
      realTimeInsights,
    };
    return this.http.post(url, JSON.stringify(body), httpOptions).pipe(retry(3));
  }

  getTranscriptions(
    spaceId: string,
    roomId?: string,
    timestamp?: string,
    size?: number,
  ): Observable<Transcription.TranscriptionsResponse> {
    const url = `${this.urlService.getDynamicUrl()}/spaces/transcriptionItems/${spaceId}`;
    const query = { roomId: roomId || '', timestamp: timestamp || '', size: size || '' };
    return this.http.get<Transcription.TranscriptionsResponse>(url, { params: query });
  }

  saveTranscript(
    spaceId: string,
    transcript: string,
    lang: string,
    metadata?: any,
    sessionId?: string,
    breakoutRoomId?: string,
  ) {
    const saveTranscriptBody = {
      transcript: transcript,
      language: lang,
      sessionId: sessionId,
      roomId: breakoutRoomId,
      metadata: metadata,
    };

    const saveURL = `${this.urlService.getDynamicUrl()}/spaces/${spaceId}/transcriptItem`;

    return this.http.patch(saveURL, JSON.stringify(saveTranscriptBody), httpOptions).pipe(retry(3));
  }

  editTranscript(spaceId: string, transcriptId: string, transcript: string) {
    const saveTranscriptBody = {
      transcript: transcript,
    };

    const saveURL = `${this.urlService.getDynamicUrl()}/spaces/${spaceId}/transcriptItem/${transcriptId}`;

    return this.http.patch(saveURL, JSON.stringify(saveTranscriptBody), httpOptions).pipe(retry(3));
  }

  deleteTranscript(spaceId: string, transcriptId: string) {
    const saveURL = `${this.urlService.getDynamicUrl()}/spaces/${spaceId}/transcriptItem/${transcriptId}`;

    return this.http.delete(saveURL, httpOptions).pipe(retry(3));
  }

  setSpaceTranscriptStatus(spaceId: string, status: boolean) {
    const url = `${this.urlService.getDynamicUrl()}/spaces/transcriptStatus/${spaceId}`;
    return this.http.patch(url, { status: status });
  }

  setSpaceTranscriptServiceType(spaceId: string, serviceType: string) {
    const url = `${this.urlService.getDynamicUrl()}/spaces/transcriptServiceType/${spaceId}`;
    return this.http.patch(url, { serviceType: serviceType });
  }

  validateIframeUrl(url: string) {
    const reqUrl = `${this.urlService.getDynamicUrl()}/spaces/url/validate`;
    return this.http.post(reqUrl, { url });
  }

  getSessionBySpace(
    spaceId: string,
    pageSize: number,
    pageNumber: number,
  ): Observable<APIAnalyticsInsightResponse> {
    const filters = btoa(
      JSON.stringify({
        spaceIds: [spaceId],
      }),
    );
    const apiUrl = `${this.urlService.getDynamicUrl()}/analytics/sessions`;
    const params = new HttpParams()
      .set('filters', filters)
      .set('pageSize', pageSize.toString())
      .set('pageNumber', pageNumber.toString())
      .set('fromFE', 'true');
    return this.http.get<APIAnalyticsInsightResponse>(apiUrl, { params });
  }

  getAnalyticsSessionById(sessionId: string): Observable<IAnalyticsInsight> {
    return this.http.get<IAnalyticsInsight>(
      `${this.urlService.getDynamicUrl()}/analytics/sessions/${sessionId}?fromFE=true`,
    );
  }

  backupSpacesToCloud(spaceIds: string[]) {
    const backupSpacesUrl = `${this.urlService.getDynamicUrl()}/spaces/backup`;
    return this.http.post(backupSpacesUrl, JSON.stringify({ spaceIds }), httpOptions);
  }

  renameSessionTitle(sessionId: string, newSessionTitle: string): Observable<any> {
    const body = JSON.stringify({ newSessionTitle });
    return this.http.patch(
      `${this.urlService.getDynamicUrl()}/analytics/sessions/${sessionId}`,
      body,
      httpOptions,
    );
  }

  endOngoingSession(spaceId: string): Observable<any> {
    return this.http.post(
      `${this.urlService.getDynamicUrl()}/spaces/${spaceId}/endOngoingSession?fromFE=true`,
      {},
      httpOptionsSkipErrorInterceptor,
    );
  }

  changeCloudRecordingState(
    RTCProvider: CallProvider,
    spaceId: string,
    RTCRoomId: string,
    state: boolean,
    participantIdentity?: string,
    recordingId?: string,
  ): Observable<any> {
    return this.http.post(
      `${this.urlService.getDynamicUrl()}/spaces/${spaceId}/changeRoomCloudRecordingState`,
      JSON.stringify({
        provider: RTCProvider,
        RTCRoomId,
        roomCloudRecordingState: state,
        participantIdentity,
        recordingId,
      }),
      httpOptionsSkipErrorInterceptor,
    );
  }

  getAttendanceEventsInSpace(spaceId: string, time: number): Observable<EventWithTimingInfo[]> {
    return this.http.get<EventWithTimingInfo[]>(
      `${this.urlService.getDynamicUrl()}/spaces/${spaceId}/event/?time=${time}`,
    );
  }

  getVideoCallAuth(
    spaceId: string,
    breakoutRoomId: string | undefined,
    context: CallContext,
    provider: CallProvider,
    opts?: RTCRoomCreationOpts,
    isObserver: boolean = false,
  ) {
    let joinRoomUrl = this.urlService.getDynamicUrl();
    switch (context) {
      case CallContext.SESSION:
        joinRoomUrl += `/spaces/rtc-auth/${spaceId}`;
        break;
    }
    let query = `?provider=${provider}`;

    if (breakoutRoomId) {
      query += `&roomId=${breakoutRoomId}`;
    }

    if (opts) {
      query += `&skipRoomCreation=${opts.skipRoomCreation}&skipGenerateToken=${opts.skipGenerateToken}`;
    }

    if (isObserver) {
      query += '&isObserver=true';
    }

    return this.http.get(joinRoomUrl + query, {
      ...httpOptions,
      observe: 'response',
    });
  }

  getSingleRecording(recordingId: string): Observable<SpaceRecording> {
    const url = `${this.urlService.getDynamicUrl()}/spaces/recordings/${recordingId}/view`;
    const headerUpdates = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }).set(
        ErrorInterceptorSkipHeader,
        '',
      ),
    };
    return this.http.get<SpaceRecording>(url, headerUpdates);
  }

  getRecordings(
    spaceId: string,
    startingAfter: string,
    endingBefore: string,
  ): Observable<{ recordings: SpaceRecording[] }> {
    const url = `${this.urlService.getDynamicUrl()}/spaces/recordings/${spaceId}?startingAfter=${startingAfter}&endingBefore=${endingBefore}`;
    return this.http.get<{ recordings: SpaceRecording[] }>(url, httpOptions);
  }

  deleteRecording(spaceId: string, recordingId: string): Observable<{ msg: string }> {
    const url = `${this.urlService.getDynamicUrl()}/spaces/recordings/${spaceId}/${recordingId}`;
    return this.http.delete<{ msg: string }>(url, httpOptions);
  }

  updateRecordingAccess(recordingId: string, access: RecordingAccess): Observable<any> {
    return this.http.patch(
      `${this.urlService.getDynamicUrl()}/spaces/recordings/${recordingId}`,
      { access },
      httpOptions,
    );
  }

  getSignedRecordingUrl(
    recordingId: string,
  ): Observable<{ downloadLink: string; recordingId: string; success: boolean }> {
    const url = `${this.urlService.getDynamicUrl()}/spaces/recordings/download/${recordingId}`;
    return this.http.get<any>(url, httpOptionsSkipErrorInterceptor);
  }

  getSignedUrl(url: string): Observable<string[]> {
    return this.http
      .post<string[]>(
        environment.resourceSignerServer,
        JSON.stringify({
          url,
        }),
        httpOptions,
      )
      .pipe(retry(3));
  }

  createBreakoutRoom(spaceId: string, rooms: Room[]) {
    const url = `${this.urlService.getDynamicUrl()}/spaces/rooms/${spaceId}`;
    const roomsWithoutPermissions = rooms.map(({ permissions, ...rest }) => rest);
    return this.http
      .post(
        url,
        JSON.stringify({
          spaceId,
          rooms: roomsWithoutPermissions,
        }),
        httpOptions,
      )
      .pipe(retry(3));
  }

  modifyBreakoutRoom(spaceId: string, room: MakeAllOptionalExcept<Room, 'uid'>) {
    const url = `${this.urlService.getDynamicUrl()}/spaces/rooms/${spaceId}`;
    return this.http.patch(url, JSON.stringify(room), httpOptions).pipe(retry(3));
  }

  deleteBreakoutRoom(spaceId: string, roomUid: string) {
    const url = `${this.urlService.getDynamicUrl()}/spaces/rooms/${spaceId}/${roomUid}`;
    return this.http.delete(url).pipe(retry(3));
  }

  queryPencilAssistant(query: string, config: any): Observable<LLMAPIResponse> {
    const url = environment.openAiServer;
    return this.http
      .post<LLMAPIResponse>(
        url,
        JSON.stringify({
          query,
          config,
        }),
        httpOptions,
      )
      .pipe(retry(3));
  }

  querySparky(
    chatHistory: Array<OpenAI.Chat.ChatCompletionMessageParam>,
    userContext: { _id: string; name: string },
  ): Observable<{ response: OpenAI.Chat.ChatCompletion.Choice }> {
    const url = `${environment.openAiServer}/sparky`;
    return this.http
      .post<{ response: OpenAI.Chat.ChatCompletion.Choice }>(
        url,
        JSON.stringify({ chatHistory, userContext }),
        httpOptions,
      )
      .pipe(retry(3));
  }

  gradeBoard(boardState: string, model: string, tutorQuery?: string) {
    const url = `${environment.openAiServer}/grader`;
    return this.http
      .post<{ response: GradingResponse }>(
        url,
        JSON.stringify({ wbState: boardState, tutorQuery, model }),
        httpOptions,
      )
      .pipe(retry(3));
  }

  getRecordingTranscript(
    recordingId: string,
  ): Observable<{ transcript: RecordingTranscriptItem[] }> {
    return this.http.get<{ transcript: RecordingTranscriptItem[] }>(
      `${this.urlService.getDynamicUrl()}/spaces/recordings/${recordingId}/transcript`,
      httpOptionsSkipErrorInterceptor,
    );
  }

  updateRecordingSummary(recordingId: string, summary: RecordingSummary): Observable<any> {
    return this.http.patch(
      `${this.urlService.getDynamicUrl()}/spaces/recordings/${recordingId}/summary`,
      JSON.stringify({ summary }),
      httpOptions,
    );
  }

  alertNSFW(sessionId: string, score: number): Observable<any> {
    const alertUrl = `${this.urlService.getDynamicUrl()}/analytics/sessions/${sessionId}/alertNSFW`;
    const body = { score };
    return this.http.post(alertUrl, body, httpOptions);
  }
}
